import type { ProductStatus } from "../api/types";

/**
 * Lifecycle state in the words a shop administrator uses.
 *
 * The backend's own vocabulary is precise but it is engineering vocabulary:
 * "unpublished" reads like a failure, and "archived" does not tell an operator
 * that the product is gone from the store. This maps each state to what it
 * actually means for the shop, and nothing here invents a state the backend
 * cannot be in.
 *
 * One deliberate conservatism: a draft is shown as "Incomplete" rather than
 * "Ready to publish". Whether a draft can be published is the backend's answer,
 * given by the validation endpoint, and it is not carried on a list row. Saying
 * "Ready to publish" here would be a guess that sends an administrator to a
 * product that then refuses to publish.
 */
const LABELS: Record<ProductStatus, string> = {
  processing: "Processing",
  needs_review: "Needs review",
  draft: "Incomplete",
  published: "Live",
  unpublished: "Hidden",
  archived: "Deleted",
};

const TONES: Record<ProductStatus, string> = {
  processing: "busy",
  needs_review: "warn",
  draft: "quiet",
  published: "live",
  unpublished: "quiet",
  archived: "quiet",
};

export function OperatorStatus({
  status,
  warningCount = 0,
}: {
  status: ProductStatus;
  warningCount?: number;
}) {
  const label = LABELS[status] ?? status;
  return (
    <span className="statusCell">
      <span className={`state state--${TONES[status] ?? "quiet"}`}>{label}</span>
      {warningCount > 0 && status !== "needs_review" ? (
        <span className="state state--warn">
          {warningCount} to review
        </span>
      ) : null}
    </span>
  );
}
